import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useLocation, useNavigate } from 'react-router-dom';


export default function AccountPage() {
  const { authFetch, user } = useAuth();
  const { tier, tierOverride, getCapStatus } = useSubscription();
  const avatarInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') === 'success') {
      setShowWelcomeModal(true);
      navigate('/account', { replace: true });
    }
  }, []);

  // ── Profile / agentschapsprofiel ─────────────────────────
  const [form, setForm] = useState({
    agency_name: '', agent_name: '', agent_slug: '', agent_bio: '',
    website_url: '', phone: '', office_address: '',
  });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [uploading, setUploading] = useState(false);

  // ── Billing ──────────────────────────────────────────────
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState('');
  const [billingAction, setBillingAction] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionError, setActionError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    authFetch('/api/settings')
      .then(r => r.json())
      .then(data => {
        setForm({
          agency_name:    data.agency_name    || '',
          agent_name:     data.agent_name     || '',
          agent_slug:     data.agent_slug     || '',
          agent_bio:      data.agent_bio      || '',
          website_url:    data.website_url    || '',
          phone:          data.phone          || '',
          office_address: data.office_address || '',
        });
        setAvatarUrl(data.avatar_url || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    authFetch('/api/account/billing-details')
      .then(r => r.json())
      .then(data => { setBilling(data); setBillingLoading(false); })
      .catch(() => { setBillingError('Billing kon niet worden geladen'); setBillingLoading(false); });
  }, []);

  const handleSave = async () => {
    setSaveError(''); setSaving(true);
    try {
      const res = await authFetch('/api/settings', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Opslaan mislukt');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('avatar', file);
    try {
      const res = await authFetch('/api/account/avatar', { method: 'POST', body: fd });
      const data = await res.json();
      if (data.avatarUrl) setAvatarUrl(data.avatarUrl);
    } catch (_) {
      // silently ignore — avatar is optional
    } finally {
      setUploading(false);
    }
  };

  const handleCheckout = async (priceId) => {
    setBillingAction(priceId);
    setActionError('');
    try {
      const res = await authFetch('/api/account/checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId }),
      });
      const data = await res.json();
      if (data?.checkoutUrl) window.location.href = data.checkoutUrl;
      else setActionError(data?.error || 'Checkout aanmaken mislukt');
    } catch (err) {
      setActionError(err.message || 'Checkout aanmaken mislukt');
    } finally {
      setBillingAction(null);
    }
  };

  const handleBillingAction = async (action, reason = '') => {
    setBillingAction(action);
    setActionError('');
    setConfirmAction(null);
    setShowCancelModal(false);
    try {
      const endpoint = action === 'cancel' ? '/api/account/cancel-subscription' : '/api/account/resume-subscription';
      const res = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'cancel' ? { reason } : {}),
      });
      const data = await res.json();
      if (!res.ok) { setActionError(data.error || 'Actie mislukt'); return; }
      const updated = await authFetch('/api/account/billing-details').then(r => r.json());
      setBilling(updated);
    } catch (err) {
      setActionError(err.message || 'Actie mislukt');
    } finally {
      setBillingAction(null);
      setCancelReason('');
    }
  };

  const initials = user?.agent_name
    ? user.agent_name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : 'A';

  const bookingUrl = `${window.location.origin}/book/${form.agent_slug}`;


  if (loading) return (
    <div className="p-8 flex items-center justify-center h-64">
      <div className="spinner spinner-blue" style={{ width: 32, height: 32 }} />
    </div>
  );

  return (
    <div className="p-8 page-enter max-w-3xl space-y-6">

      {/* ── Welcome modal (shown after successful Stripe checkout) ── */}
      {showWelcomeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center space-y-4">
            <div className="text-6xl">🎉</div>
            <h2 className="text-2xl font-bold text-gray-900">Welkom bij TravelAIgent!</h2>
            <p className="text-gray-500 text-sm leading-relaxed">
              Uw abonnement is actief. U kunt nu direct aan de slag — stel uw AI-instructies in, maak uw eerste reisvoorstel, of koppel uw agenda.
            </p>
            <p className="text-gray-400 text-xs">
              Vragen? Gebruik de chat of raadpleeg de FAQ.
            </p>
            <button
              onClick={() => setShowWelcomeModal(false)}
              className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-opacity hover:opacity-90"
              style={{ background: 'var(--brand, #2dd4bf)' }}
            >
              Aan de slag
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Account</h1>
        <p className="text-gray-500 text-sm mt-1">Beheer uw profiel, agentschap en abonnement</p>
      </div>

      {/* ── Profile picture + identity ─────────────── */}
      <section className="pro-card p-5">
        <h2 className="font-semibold text-gray-900 mb-4">Profielfoto</h2>
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Profielfoto"
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-semibold"
                style={{ backgroundColor: 'var(--brand)' }}
              >
                {initials}
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                <div className="spinner" style={{ width: 20, height: 20, borderColor: 'white', borderTopColor: 'transparent' }} />
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{user?.agent_name || '—'}</p>
            <p className="text-sm text-gray-500">{user?.email}</p>
            <button
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploading}
              className="mt-2 text-sm text-blue-600 hover:underline disabled:opacity-50"
            >
              {uploading ? 'Uploaden...' : 'Foto wijzigen'}
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleAvatarChange}
            />
          </div>
        </div>
      </section>

      {/* ── Agentschapsprofiel ─────────────────────── */}
      <section className="pro-card p-5">
        <h2 className="font-semibold text-gray-900 mb-4">Agentschapsprofiel</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Naam agentschap</label>
            <input
              className="input"
              value={form.agency_name}
              onChange={e => setForm({ ...form, agency_name: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Uw naam</label>
            <input
              className="input"
              value={form.agent_name}
              onChange={e => setForm({ ...form, agent_name: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="label">Agent bio (getoond op boekingspagina)</label>
            <textarea
              className="input min-h-20"
              placeholder="Ik ben gespecialiseerd in gezinsvakanties en Caraïbische cruises."
              value={form.agent_bio}
              onChange={e => setForm({ ...form, agent_bio: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Website URL</label>
            <input
              type="url"
              className="input"
              placeholder="https://www.uw-agentschap.nl"
              value={form.website_url}
              onChange={e => setForm({ ...form, website_url: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Telefoonnummer</label>
            <input
              className="input"
              placeholder="+31 20 123 4567"
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="label">Kantooradres</label>
            <input
              className="input"
              placeholder="Keizersgracht 123, 1015 Amsterdam"
              value={form.office_address}
              onChange={e => setForm({ ...form, office_address: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="label">Boekingspagina slug</label>
            <div className="flex items-center">
              <span className="px-3 py-2 bg-gray-50 border border-r-0 border-gray-200 rounded-l-lg text-sm text-gray-500 whitespace-nowrap">
                {window.location.origin}/book/
              </span>
              <input
                className="input rounded-l-none"
                value={form.agent_slug}
                onChange={e => setForm({ ...form, agent_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Uw publieke boekingslink:{' '}
              <a href={bookingUrl} target="_blank" rel="noreferrer" className="underline" style={{ color: 'var(--brand)' }}>
                {bookingUrl}
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* ── Abonnement ─────────────────────────────── */}
      <section className="pro-card p-5">
        <h2 className="font-semibold text-gray-900 mb-1">Abonnement</h2>
        <p className="text-sm text-gray-500 mb-5">Kies het pakket dat bij uw reisagentschap past.</p>

        {billingLoading ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <div className="spinner" style={{ width: 16, height: 16 }} />
            <span>Abonnement laden...</span>
          </div>
        ) : billingError ? (
          <p className="text-sm text-red-600">{billingError}</p>
        ) : (() => {
          const sub = billing?.subscription;
          const isCanceling = sub?.cancelAtPeriodEnd;
          const hasPaidTier = tier === 'assistent' || tier === 'bureau';
          const periodEnd = sub?.currentPeriodEnd
            ? new Date(sub.currentPeriodEnd).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
            : null;

          const PLANS = [
            {
              id: 'assistent',
              priceId: 'price_1TMUJwDx4D9iXVqTuMyeDjLM',
              name: 'Assistent',
              price: '€29',
              features: [
                'AI Chat (onbeperkt)',
                'Afspraken / basisagenda (onbeperkt)',
                'FAQ Inbox (15 antwoorden/maand)',
                'Reisvoorstellen (5/maand)',
              ],
              dark: false,
            },
            {
              id: 'bureau',
              priceId: 'price_1TMULdDx4D9iXVqTJmeBNRb3',
              name: 'Bureau',
              price: '€89',
              features: [
                'Alles uit Assistent, onbeperkt',
                'Boekingen + PDF-extractie + verrijking',
                'Reisbescheiden',
                'Klanten + klantinzichten',
                'Nieuwsbrieven & marketing',
                'Auto-emails (pre- en post-trip)',
                'Volledige AI-instructies + trainingsdocumenten',
                'Afspraken met AI e-mailgeneratie',
              ],
              dark: true,
            },
          ];

          const buttonsDisabled = hasPaidTier && !isCanceling;

          return (
            <div className="space-y-4">
              {actionError && <p className="text-sm text-red-600">{actionError}</p>}

              {/* Current subscription banner */}
              {hasPaidTier && !isCanceling && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
                  {/* Top row: plan name + active badge */}
                  <div className="p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-0.5">Huidig abonnement</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {tier === 'assistent' ? 'Assistent — €29/maand' : 'Bureau — €89/maand'}
                        {sub?.status === 'trialing' && <span className="ml-2 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Proefperiode</span>}
                      </p>
                      {periodEnd && <p className="text-xs text-gray-400 mt-0.5">Volgende factuurdatum: {periodEnd}</p>}
                      {tier === 'assistent' && <p className="text-xs text-amber-600 mt-1.5">Wilt u upgraden naar Bureau? Zeg dan eerst uw huidige abonnement op — daarna kunt u direct een nieuw pakket kiezen.</p>}
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-green-100 text-green-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                      Actief
                    </span>
                  </div>

                  {/* Usage bars — only shown when caps apply (Assistent tier) */}
                  {(() => {
                    const features = [
                      { key: 'proposals', label: 'Reisvoorstellen' },
                      { key: 'faq_replies', label: 'FAQ antwoorden' },
                    ].filter(f => getCapStatus(f.key).cap !== null);
                    if (features.length === 0) return null;
                    return (
                      <div className="px-4 pb-4 pt-3 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Gebruik deze maand</p>
                        <div className="space-y-3">
                          {features.map(({ key, label }) => {
                            const capInfo = getCapStatus(key);
                            const barColor = capInfo.percentage >= 100 ? '#ef4444' : capInfo.percentage >= 60 ? '#f59e0b' : 'var(--brand)';
                            return (
                              <div key={key}>
                                <div className="flex justify-between mb-1">
                                  <span className="text-xs text-gray-500">{label}</span>
                                  <span className="text-xs text-gray-400">{capInfo.used ?? 0} / {capInfo.cap}</span>
                                </div>
                                <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${Math.min(capInfo.percentage, 100)}%`, background: barColor, borderRadius: 2, transition: 'width 0.3s' }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Canceling banner */}
              {hasPaidTier && isCanceling && periodEnd && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-sm text-orange-800">
                    Abonnement loopt af op <strong>{periodEnd}</strong>. Daarna kunt u een nieuw pakket kiezen.
                  </p>
                  <button
                    onClick={() => handleBillingAction('resume')}
                    disabled={!!billingAction}
                    className="btn-primary text-sm shrink-0 disabled:opacity-50"
                  >
                    {billingAction === 'resume' ? 'Bezig...' : 'Hervatten'}
                  </button>
                </div>
              )}

              {/* Cancel modal */}
              {showCancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
                  <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-5">
                    <div className="text-center">
                      <div className="text-3xl mb-3">😢</div>
                      <h3 className="text-lg font-bold text-gray-900">Jammer dat u vertrekt</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Uw abonnement blijft actief tot het einde van de huidige betaalperiode{periodEnd ? ` (${periodEnd})` : ''}.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Mogen wij vragen waarom u opzegt? <span className="text-gray-400 font-normal">(optioneel)</span>
                      </label>
                      <textarea
                        className="input min-h-24 resize-none"
                        placeholder="Bijv. te duur, miste een functie, gebruik het niet genoeg..."
                        value={cancelReason}
                        onChange={e => setCancelReason(e.target.value)}
                      />
                      <p className="text-xs text-gray-400 mt-1.5">Uw feedback helpt ons om TravelAIgent te blijven verbeteren.</p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleBillingAction('cancel', cancelReason)}
                        disabled={!!billingAction}
                        className="w-full py-2.5 rounded-xl text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        {billingAction === 'cancel' ? 'Bezig...' : 'Abonnement opzeggen'}
                      </button>
                      <button
                        onClick={() => { setShowCancelModal(false); setCancelReason(''); }}
                        className="w-full py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 border border-gray-200 transition-colors"
                      >
                        Toch niet, blijf bij TravelAIgent
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {PLANS.map(plan => {
                  const isDisabled = buttonsDisabled;

                  return (
                    <div
                      key={plan.id}
                      className="rounded-xl overflow-hidden"
                      style={plan.dark ? { background: '#0f172a' } : { background: '#f8fafc', border: '1px solid #e2e8f0' }}
                    >
                      {/* Most popular badge */}
                      {plan.dark && (
                        <div className="flex justify-center pt-4">
                          <span style={{ background: '#2dd4bf', color: '#0f172a' }} className="text-xs font-bold px-4 py-1.5 rounded-full tracking-widest uppercase">
                            Meest gekozen
                          </span>
                        </div>
                      )}

                      <div className="p-6">
                        <h3 className={`text-xl font-bold mb-1 ${plan.dark ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                        <div className="mb-5">
                          <span className={`text-4xl font-extrabold ${plan.dark ? 'text-white' : 'text-gray-900'}`}>{plan.price}</span>
                          <span className={`text-sm ml-1 ${plan.dark ? 'text-gray-400' : 'text-gray-500'}`}> / maand</span>
                        </div>

                        <ul className="space-y-2.5 mb-6">
                          {plan.features.map((f, i) => (
                            <li key={i} className={`flex items-start gap-2.5 text-sm ${plan.dark ? 'text-gray-300' : 'text-gray-600'}`}>
                              <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#2dd4bf' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                              {f}
                            </li>
                          ))}
                        </ul>

                        {tierOverride ? (
                          <p className="text-xs text-gray-400 text-center">Beheerd door administrator</p>
                        ) : isDisabled ? (
                          <button
                            disabled
                            className="w-full py-3 rounded-xl text-sm font-semibold cursor-not-allowed"
                            style={{ background: '#e2e8f0', color: '#94a3b8' }}
                          >
                            {plan.dark ? 'Start met Bureau' : 'Start met Assistent'}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleCheckout(plan.priceId)}
                            disabled={!!billingAction}
                            className="w-full py-3 rounded-xl text-sm font-semibold transition-opacity disabled:opacity-60"
                            style={plan.dark
                              ? { background: '#2dd4bf', color: '#0f172a' }
                              : { background: 'transparent', color: '#0f172a', border: '2px solid #0f172a' }
                            }
                          >
                            {billingAction === plan.priceId ? 'Bezig...' : plan.dark ? 'Start met Bureau' : 'Start met Assistent'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Cancel link — only shown when active (not already canceling) */}
              {hasPaidTier && !isCanceling && !tierOverride && (
                <div className="text-center pt-1">
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="text-sm text-red-500 hover:text-red-700 underline underline-offset-2"
                  >
                    Abonnement opzeggen
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </section>

      {/* ── Save ───────────────────────────────────── */}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm px-3 py-2">{saveError}</div>
      )}
      <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
        {saving ? <span className="spinner" /> : null}
        {saving ? 'Opslaan...' : saved ? 'Opgeslagen!' : 'Profiel opslaan'}
      </button>
    </div>
  );
}
